import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Card } from '../components/ui'
import { QuickAllocateModal, CameraScanner, CreateVolunteerModal, CreatedVolunteer } from '../components'
import { useAuth } from '../routes/auth'
import {
    CheckInPageMeta,
    API_BASE_URL
} from '../data'
import { Event, CheckInPageMetaProps, AccessColor, CheckInAreaWithShifts, CheckInShiftLead, ShiftVolunteersResponse } from '../data/types'
import {
    CheckCircleIcon,
    XCircleIcon,
    PersonIcon,
    LocationIcon,
    ChevronLeftIcon,
    SearchIcon,
    ScanIcon,
    PlusIcon,
    CameraIcon,
    ListIcon
} from '../icons'
import { getShiftDisplayName, sortByName, sortShifts } from '../utils/sorting'

type CheckInMode = 'scan' | 'search' | 'shift'

type CheckInVolunteer = {
    csid: number
    first_name: string
    last_name: string
    email: string
    phone: string
    country: string
    jamat_khana: string
    gender: string
    profile_photo?: string
    international_volunteer?: boolean
    eligible_for_duty?: boolean
}

type CheckInAllocation = {
    id: number
    shift: {
        id: number
        title: string
        lead: CheckInShiftLead
        deputy_lead: CheckInShiftLead
        access_color: AccessColor
        duty: {
            id: number
            name: string
        } | null
        area: {
            id: number
            name: string
            access_color: AccessColor
            event: {
                id: number
                name: string
                start_date: string
                end_date: string
            }
        }
    }
    allocation_times: Array<{
        id: number
        start_time: string
        end_time: string
        status: boolean | null
        checked_in_at: string | null
        checked_out_at: string | null
        checked_in_by: {
            id: number
            name: string
        } | null
    }>
}

type CheckInResult = {
    volunteer: CheckInVolunteer
    allocations: CheckInAllocation[]
}

const LOCAL_STORAGE_EVENT_KEY = 'checkin_selected_events'

export const CheckIn = () => {
    const meta: CheckInPageMetaProps = CheckInPageMeta
    const { user } = useAuth()

    const [mode, setMode] = useState<CheckInMode>('scan')
    const [scanInput, setScanInput] = useState('')
    const [searchInput, setSearchInput] = useState('')
    const [searchResults, setSearchResults] = useState<CheckInResult[]>([])
    const [events, setEvents] = useState<Event[]>([])
    const [selectedEventIds, setSelectedEventIds] = useState<number[]>([])
    const [isEventDropdownOpen, setIsEventDropdownOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [searching, setSearching] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [result, setResult] = useState<CheckInResult | null>(null)
    const [checkingInId, setCheckingInId] = useState<number | null>(null)
    const [deallocatingId, setDeallocatingId] = useState<number | null>(null)
    const [successMessage, setSuccessMessage] = useState<string | null>(null)
    const [isQuickAllocateOpen, setIsQuickAllocateOpen] = useState(false)
    const [hasSearched, setHasSearched] = useState(false)
    const [showCameraScanner, setShowCameraScanner] = useState(false)
    const [isCreateVolunteerOpen, setIsCreateVolunteerOpen] = useState(false)

    // Shift View state
    const [areas, setAreas] = useState<CheckInAreaWithShifts[]>([])
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null)
    const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null)
    const [shiftVolunteers, setShiftVolunteers] = useState<ShiftVolunteersResponse | null>(null)
    const [loadingShiftVolunteers, setLoadingShiftVolunteers] = useState(false)
    const [shiftViewCheckingInId, setShiftViewCheckingInId] = useState<number | null>(null)

    const inputRef = useRef<HTMLInputElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const eventDropdownRef = useRef<HTMLDivElement>(null)

    const canAllocate = user?.permissions?.includes('manage_allocations')

    // Load events on mount
    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/event`, {
                    credentials: 'include'
                })
                if (response.ok) {
                    const data = await response.json()
                    let eventsList = data.value || data
                    if (Array.isArray(eventsList) && eventsList.length > 0 && Array.isArray(eventsList[0])) {
                        eventsList = eventsList[0]
                    }
                    setEvents(eventsList)

                    // Load saved selection from localStorage or default to all events
                    const savedEventIds = localStorage.getItem(LOCAL_STORAGE_EVENT_KEY)
                    if (savedEventIds) {
                        try {
                            const parsedIds = JSON.parse(savedEventIds) as number[]
                            // Filter to only include IDs that still exist
                            const validIds = parsedIds.filter((id: number) =>
                                eventsList.some((e: Event) => e.id === id)
                            )
                            if (validIds.length > 0) {
                                setSelectedEventIds(validIds)
                            } else {
                                // Default to all events if no valid saved selection
                                setSelectedEventIds(eventsList.map((e: Event) => e.id))
                            }
                        } catch {
                            // Invalid JSON, default to all events
                            setSelectedEventIds(eventsList.map((e: Event) => e.id))
                        }
                    } else {
                        // No saved selection, default to all events
                        setSelectedEventIds(eventsList.map((e: Event) => e.id))
                    }
                }
            } catch (err) {
                console.error('Failed to fetch events:', err)
            }
        }
        fetchEvents()
    }, [])

    // Save event selection to localStorage
    useEffect(() => {
        if (selectedEventIds.length > 0) {
            localStorage.setItem(LOCAL_STORAGE_EVENT_KEY, JSON.stringify(selectedEventIds))
        } else {
            localStorage.removeItem(LOCAL_STORAGE_EVENT_KEY)
        }
    }, [selectedEventIds])

    // Auto-focus input on mount and after actions
    useEffect(() => {
        if (mode === 'scan') {
            inputRef.current?.focus()
        } else {
            searchInputRef.current?.focus()
        }
    }, [result, mode])

    // Clear success message after delay
    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(null), 3000)
            return () => clearTimeout(timer)
        }
    }, [successMessage])

    // Clear search results when switching modes
    useEffect(() => {
        setSearchResults([])
        setSearchInput('')
        setScanInput('')
        setError(null)
        setHasSearched(false)
        // Reset shift view state when switching modes
        if (mode !== 'shift') {
            setShiftVolunteers(null)
        }
    }, [mode])

    // Fetch areas when selected events change (for shift view)
    useEffect(() => {
        const fetchAreas = async () => {
            if (selectedEventIds.length === 0) {
                setAreas([])
                setSelectedAreaId(null)
                setSelectedShiftId(null)
                return
            }

            try {
                const params = new URLSearchParams()
                selectedEventIds.forEach(id => params.append('event_ids[]', id.toString()))

                const response = await fetch(`${API_BASE_URL}/check-in/areas?${params}`, {
                    credentials: 'include'
                })

                if (response.ok) {
                    const data: CheckInAreaWithShifts[] = await response.json()
                    setAreas(data)
                    // Reset selection if current area is not in new list
                    if (selectedAreaId && !data.some(a => a.id === selectedAreaId)) {
                        setSelectedAreaId(null)
                        setSelectedShiftId(null)
                    }
                }
            } catch (err) {
                console.error('Failed to fetch areas:', err)
            }
        }

        fetchAreas()
    }, [selectedEventIds, selectedAreaId])

    // Fetch volunteers when shift is selected
    useEffect(() => {
        const fetchShiftVolunteers = async () => {
            if (!selectedShiftId) {
                setShiftVolunteers(null)
                return
            }

            setLoadingShiftVolunteers(true)
            setError(null)

            try {
                const response = await fetch(`${API_BASE_URL}/check-in/shift/${selectedShiftId}/volunteers`, {
                    credentials: 'include'
                })

                if (response.ok) {
                    const data: ShiftVolunteersResponse = await response.json()
                    setShiftVolunteers(data)
                } else {
                    const errorData = await response.json()
                    setError(errorData.error || 'Failed to load volunteers')
                }
            } catch (err) {
                console.error('Failed to fetch shift volunteers:', err)
                setError('Failed to load volunteers')
            } finally {
                setLoadingShiftVolunteers(false)
            }
        }

        fetchShiftVolunteers()
    }, [selectedShiftId])

    // Close event dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (eventDropdownRef.current && !eventDropdownRef.current.contains(event.target as Node)) {
                setIsEventDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleSearch = useCallback(async () => {
        if (searchInput.trim().length < 2) return

        setSearching(true)
        setError(null)
        setSearchResults([])
        setResult(null)
        setHasSearched(true)

        try {
            const params = new URLSearchParams({ query: searchInput.trim() })
            selectedEventIds.forEach(id => params.append('event_ids[]', id.toString()))

            const response = await fetch(`${API_BASE_URL}/check-in/search?${params}`, {
                credentials: 'include'
            })

            if (!response.ok) {
                const data = await response.json()
                throw new Error(data.error || meta.errorMessage)
            }

            const data: CheckInResult[] = await response.json()
            setSearchResults(data)
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setSearching(false)
        }
    }, [searchInput, selectedEventIds, meta])

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSearch()
        }
    }

    const handleSelectVolunteer = (selectedResult: CheckInResult) => {
        setResult(null) // Clear any previous result first
        setResult(selectedResult)
        setSearchResults([])
        setSearchInput('')
        setHasSearched(false)
    }

    const handleEventToggle = (eventId: number) => {
        setSelectedEventIds(prev => {
            if (prev.includes(eventId)) {
                return prev.filter(id => id !== eventId)
            } else {
                return [...prev, eventId]
            }
        })
        // Reset all check-in state when changing events
        setResult(null)
        setSearchResults([])
        setSearchInput('')
        setScanInput('')
        setHasSearched(false)
        setError(null)
        setSuccessMessage(null)
    }

    const handleSelectAllEvents = () => {
        setSelectedEventIds(events.map(e => e.id))
        // Reset state
        setResult(null)
        setSearchResults([])
        setHasSearched(false)
    }

    const handleDeselectAllEvents = () => {
        setSelectedEventIds([])
        // Reset state
        setResult(null)
        setSearchResults([])
        setHasSearched(false)
    }

    const refreshVolunteerAllocations = useCallback(async (volunteerCsid: number) => {
        try {
            const params = new URLSearchParams()
            if (selectedEventIds.length > 0) {
                params.append('event_ids', selectedEventIds.join(','))
            }

            const response = await fetch(`${API_BASE_URL}/check-in/volunteer/${volunteerCsid}?${params}`, {
                credentials: 'include'
            })

            if (response.ok) {
                const data: CheckInResult = await response.json()
                setResult(data)
            }
        } catch (err) {
            console.error('Failed to refresh allocations:', err)
        }
    }, [selectedEventIds])

    const handleScan = useCallback(async (inputValue: string) => {
        if (!inputValue.trim()) return

        setLoading(true)
        setError(null)
        setResult(null)

        try {
            // Try to parse JSON from QR code (format: {"id": "12345678"})
            let barcode = inputValue.trim()
            try {
                const parsed = JSON.parse(barcode)
                if (parsed.id) {
                    barcode = String(parsed.id)
                }
            } catch {
                // Not JSON, use as-is (might be plain barcode)
            }

            const response = await fetch(`${API_BASE_URL}/check-in/lookup`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    barcode,
                    event_ids: selectedEventIds
                })
            })

            if (!response.ok) {
                const data = await response.json()
                throw new Error(data.error || meta.volunteerNotFoundMessage)
            }

            const data: CheckInResult = await response.json()
            setResult(data)
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setLoading(false)
            setScanInput('')
        }
    }, [selectedEventIds, meta])

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleScan(scanInput)
        }
    }

    const handleCheckIn = async (allocationTimeId: number) => {
        setCheckingInId(allocationTimeId)
        setError(null)

        try {
            const response = await fetch(`${API_BASE_URL}/check-in/allocation/${allocationTimeId}`, {
                method: 'POST',
                credentials: 'include'
            })

            if (!response.ok) {
                const data = await response.json()
                throw new Error(data.error || meta.checkInErrorMessage)
            }

            const data = await response.json()

            // Update the local state with the check-in info
            setResult(prev => {
                if (!prev) return null
                return {
                    ...prev,
                    allocations: prev.allocations.map(allocation => ({
                        ...allocation,
                        allocation_times: allocation.allocation_times.map(time =>
                            time.id === allocationTimeId
                                ? {
                                    ...time,
                                    checked_in_at: data.allocation_time.checked_in_at,
                                    checked_in_by: data.allocation_time.checked_in_by
                                }
                                : time
                        )
                    }))
                }
            })

            setSuccessMessage(meta.checkInSuccessMessage)
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.checkInErrorMessage)
        } finally {
            setCheckingInId(null)
        }
    }

    const handleUndoCheckIn = async (allocationTimeId: number) => {
        setCheckingInId(allocationTimeId)
        setError(null)

        try {
            const response = await fetch(`${API_BASE_URL}/check-in/allocation/${allocationTimeId}`, {
                method: 'DELETE',
                credentials: 'include'
            })

            if (!response.ok) {
                const data = await response.json()
                throw new Error(data.error || meta.checkInErrorMessage)
            }

            // Update the local state
            setResult(prev => {
                if (!prev) return null
                return {
                    ...prev,
                    allocations: prev.allocations.map(allocation => ({
                        ...allocation,
                        allocation_times: allocation.allocation_times.map(time =>
                            time.id === allocationTimeId
                                ? { ...time, checked_in_at: null, checked_in_by: null }
                                : time
                        )
                    }))
                }
            })

            setSuccessMessage(meta.undoCheckInSuccessMessage)
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.checkInErrorMessage)
        } finally {
            setCheckingInId(null)
        }
    }

    const handleDeallocateDuty = async ({
        shiftId,
        volunteerId,
        allocationTimeId,
        dutyName,
        status,
        source,
    }: {
        shiftId: number
        volunteerId: number
        allocationTimeId: number
        dutyName: string
        status: boolean | null
        source: 'result' | 'shift'
    }) => {
        if (!canAllocate) return
        if (!window.confirm(meta.deallocateDutyConfirmMessage(dutyName))) return

        setDeallocatingId(allocationTimeId)
        setError(null)

        try {
            const response = await fetch(`${API_BASE_URL}/allocation/${shiftId}/${volunteerId}/${allocationTimeId}`, {
                method: 'DELETE',
                credentials: 'include'
            })

            if (!response.ok) {
                let errorMessage = meta.deallocateDutyErrorMessage
                try {
                    const data = await response.json()
                    errorMessage = data.message || data.error || errorMessage
                } catch {
                    // Response may be empty.
                }
                throw new Error(errorMessage)
            }

            if (source === 'result') {
                setResult(prev => {
                    if (!prev) return null
                    return {
                        ...prev,
                        allocations: prev.allocations
                            .map(allocation => ({
                                ...allocation,
                                allocation_times: allocation.allocation_times.filter(time => time.id !== allocationTimeId)
                            }))
                            .filter(allocation => allocation.allocation_times.length > 0)
                    }
                })
            } else {
                setShiftVolunteers(prev => {
                    if (!prev) return null
                    return {
                        ...prev,
                        volunteers: prev.volunteers.filter(volunteer => volunteer.allocation_time_id !== allocationTimeId),
                        stats: {
                            ...prev.stats,
                            total: Math.max(0, prev.stats.total - 1),
                            accepted: status === true ? Math.max(0, prev.stats.accepted - 1) : prev.stats.accepted,
                        }
                    }
                })
            }

            setSuccessMessage(meta.deallocateDutySuccessMessage)
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.deallocateDutyErrorMessage)
        } finally {
            setDeallocatingId(null)
        }
    }

    const handleScanAnother = () => {
        setResult(null)
        setError(null)
        setScanInput('')
        setSearchInput('')
        setSearchResults([])
        setHasSearched(false)
        if (mode === 'scan') {
            inputRef.current?.focus()
        } else {
            searchInputRef.current?.focus()
        }
    }

    const handleVolunteerCreated = (volunteer: CreatedVolunteer) => {
        // Set the newly created volunteer as the current result
        setResult({
            volunteer: {
                csid: volunteer.csid,
                first_name: volunteer.first_name,
                last_name: volunteer.last_name,
                email: volunteer.email,
                phone: volunteer.phone,
                country: volunteer.country,
                jamat_khana: volunteer.jamat_khana,
                gender: volunteer.gender,
                profile_photo: volunteer.profile_photo
            },
            allocations: []
        })
        setSearchResults([])
        setSearchInput('')
        setHasSearched(false)
        // Automatically open the allocation modal so they can assign a shift
        if (canAllocate && selectedEventIds.length > 0) {
            setIsQuickAllocateOpen(true)
        }
    }

    // Shift View check-in handler
    const handleShiftViewCheckIn = async (allocationTimeId: number) => {
        setShiftViewCheckingInId(allocationTimeId)
        setError(null)

        try {
            const response = await fetch(`${API_BASE_URL}/check-in/allocation/${allocationTimeId}`, {
                method: 'POST',
                credentials: 'include'
            })

            if (!response.ok) {
                const data = await response.json()
                throw new Error(data.error || meta.checkInErrorMessage)
            }

            const data = await response.json()

            // Update the local state
            setShiftVolunteers(prev => {
                if (!prev) return null
                return {
                    ...prev,
                    volunteers: prev.volunteers.map(v =>
                        v.allocation_time_id === allocationTimeId
                            ? {
                                ...v,
                                checked_in_at: data.allocation_time.checked_in_at,
                                checked_in_by: data.allocation_time.checked_in_by
                            }
                            : v
                    ),
                    stats: {
                        ...prev.stats,
                        checked_in: prev.stats.checked_in + 1
                    }
                }
            })

            setSuccessMessage(meta.checkInSuccessMessage)
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.checkInErrorMessage)
        } finally {
            setShiftViewCheckingInId(null)
        }
    }

    // Shift View undo check-in handler
    const handleShiftViewUndoCheckIn = async (allocationTimeId: number) => {
        setShiftViewCheckingInId(allocationTimeId)
        setError(null)

        try {
            const response = await fetch(`${API_BASE_URL}/check-in/allocation/${allocationTimeId}`, {
                method: 'DELETE',
                credentials: 'include'
            })

            if (!response.ok) {
                const data = await response.json()
                throw new Error(data.error || meta.checkInErrorMessage)
            }

            // Update the local state
            setShiftVolunteers(prev => {
                if (!prev) return null
                return {
                    ...prev,
                    volunteers: prev.volunteers.map(v =>
                        v.allocation_time_id === allocationTimeId
                            ? { ...v, checked_in_at: null, checked_in_by: null }
                            : v
                    ),
                    stats: {
                        ...prev.stats,
                        checked_in: prev.stats.checked_in - 1
                    }
                }
            })

            setSuccessMessage(meta.undoCheckInSuccessMessage)
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.checkInErrorMessage)
        } finally {
            setShiftViewCheckingInId(null)
        }
    }

    // Handle area selection change
    const handleAreaChange = (areaId: number | null) => {
        setSelectedAreaId(areaId)
        setSelectedShiftId(null)
        setShiftVolunteers(null)
    }

    // Get shifts for selected area
    const selectedAreaShifts = selectedAreaId
        ? areas.find(a => a.id === selectedAreaId)?.shifts || []
        : []

    const formatLeadInfo = (lead: CheckInShiftLead, role: string) => {
        if (!lead?.name && !lead?.phone) return null
        return `${role}: ${lead?.name || 'Unassigned'}${lead?.phone ? ` (${lead.phone})` : ''}`
    }

    const getShiftLeadInfo = (lead: CheckInShiftLead, deputyLead: CheckInShiftLead) => {
        return [formatLeadInfo(lead, 'Lead'), formatLeadInfo(deputyLead, 'Deputy')].filter((info): info is string => Boolean(info))
    }

    const formatShiftOption = (shift: CheckInAreaWithShifts['shifts'][number]) => {
        const leadInfo = getShiftLeadInfo(shift.lead, shift.deputy_lead)

        return leadInfo.length > 0
            ? `${getShiftDisplayName(shift)} - ${leadInfo.join(' | ')}`
            : getShiftDisplayName(shift)
    }

    const formatCheckedInTime = (isoString: string) => {
        const date = new Date(isoString)
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }

    const getStatusBadge = (status: boolean | null) => {
        if (status === null) {
            return (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                    {meta.pendingStatusLabel}
                </span>
            )
        }
        if (status) {
            return (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400">
                    {meta.acceptedStatusLabel}
                </span>
            )
        }
        return (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400">
                {meta.declinedStatusLabel}
            </span>
        )
    }

    // Count checked-in allocations
    const checkedInCount = result?.allocations.filter(a => a.allocation_times[0].status !== false).reduce((total, allocation) =>
        total + allocation.allocation_times.filter(t => t.checked_in_at).length, 0) || 0
    const totalAllocations = result?.allocations.filter(a => a.allocation_times[0].status !== false).reduce((total, allocation) =>
        total + allocation.allocation_times.length, 0) || 0
    const selectedShiftLeadInfo = shiftVolunteers
        ? getShiftLeadInfo(shiftVolunteers.shift.lead, shiftVolunteers.shift.deputy_lead)
        : []

    return (
        <div className="flex-1 pt-8 pb-8">
            <div className="container mx-auto px-4 max-w-6xl">
                {/* Back to Admin Link */}
                {user?.role !== 'checkin_volunteer' &&
                    <Link
                        to="/admin"
                        className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 mb-6"
                    >
                        <ChevronLeftIcon className="w-5 h-5 me-1" />
                        {meta.backToAdmin}
                    </Link>
                }

                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                        {meta.title}
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        {meta.description}
                    </p>
                </div>

                {/* Scanner/Search Input and Event Filter */}
                <Card className="mb-6 overflow-visible">
                    <div className="p-6">
                        {/* Mode Toggle */}
                        <div className="flex mb-4 border-b border-gray-200 dark:border-gray-700">
                            <button
                                onClick={() => setMode('scan')}
                                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${mode === 'scan'
                                    ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                    }`}
                            >
                                <span className="flex items-center gap-1">
                                    <ScanIcon className="w-4 h-4" />
                                    {meta.scanModeLabel}
                                </span>
                            </button>
                            <button
                                onClick={() => setMode('search')}
                                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${mode === 'search'
                                    ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                    }`}
                            >
                                <span className="flex items-center gap-1">
                                    <SearchIcon className="w-4 h-4" />
                                    {meta.searchModeLabel}
                                </span>
                            </button>
                            {user?.role === 'admin' && <button
                                onClick={() => setMode('shift')}
                                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${mode === 'shift'
                                    ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                    }`}
                            >
                                <span className="flex items-center gap-1">
                                    <ListIcon className="w-4 h-4" />
                                    {meta.shiftViewModeLabel}
                                </span>
                            </button>}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Scanner/Search Input or Shift View Dropdowns */}
                            <div className="md:col-span-2">
                                {mode === 'scan' ? (
                                    <>
                                        <label htmlFor="scanner-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {meta.scannerLabel}
                                        </label>
                                        <div className="relative">
                                            <input
                                                ref={inputRef}
                                                id="scanner-input"
                                                type="text"
                                                value={scanInput}
                                                onChange={(e) => setScanInput(e.target.value)}
                                                onKeyDown={handleKeyDown}
                                                placeholder={meta.scannerPlaceholder}
                                                className="w-full h-[50px] px-4 pe-12 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                autoComplete="off"
                                                autoFocus
                                            />
                                            <button
                                                onClick={() => setShowCameraScanner(true)}
                                                className="absolute end-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 focus:outline-none"
                                                title={meta.cameraScanButton}
                                                type="button"
                                            >
                                                <CameraIcon className="w-6 h-6" />
                                            </button>
                                        </div>
                                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                            {meta.scannerHint}
                                        </p>
                                    </>
                                ) : mode === 'search' ? (
                                    <>
                                        <label htmlFor="search-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            {meta.searchLabel}
                                        </label>
                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <input
                                                ref={searchInputRef}
                                                id="search-input"
                                                type="text"
                                                value={searchInput}
                                                onChange={(e) => setSearchInput(e.target.value)}
                                                onKeyDown={handleSearchKeyDown}
                                                placeholder={meta.searchPlaceholder}
                                                className="flex-1 h-[50px] px-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                                autoComplete="off"
                                            />
                                            <button
                                                onClick={handleSearch}
                                                disabled={searching || searchInput.trim().length < 2}
                                                className="w-full sm:w-auto h-[50px] px-6 text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                            >
                                                {searching ? meta.searchingText : meta.searchButton}
                                            </button>
                                        </div>
                                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                            {meta.searchHint}
                                        </p>
                                    </>
                                ) : (
                                    /* Shift View - Area and Shift dropdowns */
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {/* Area Dropdown */}
                                        <div>
                                            <label htmlFor="area-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                {meta.shiftViewAreaLabel}
                                            </label>
                                            <select
                                                id="area-select"
                                                value={selectedAreaId || ''}
                                                onChange={(e) => handleAreaChange(e.target.value ? parseInt(e.target.value) : null)}
                                                className="w-full h-[50px] px-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                            >
                                                <option value="">{meta.shiftViewSelectAreaPrompt}</option>
                                                {sortByName(areas).map(area => (
                                                    <option key={area.id} value={area.id}>
                                                        {area.name} ({area.event_name})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {/* Shift Dropdown */}
                                        <div>
                                            <label htmlFor="shift-select" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                {meta.shiftViewShiftLabel}
                                            </label>
                                            <select
                                                id="shift-select"
                                                value={selectedShiftId || ''}
                                                onChange={(e) => setSelectedShiftId(e.target.value ? parseInt(e.target.value) : null)}
                                                disabled={!selectedAreaId}
                                                className="w-full h-[50px] px-4 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <option value="">{meta.shiftViewSelectShiftPrompt}</option>
                                                {sortShifts(selectedAreaShifts).map(shift => (
                                                    <option key={shift.id} value={shift.id}>
                                                        {formatShiftOption(shift)}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Event Filter - Multi-select */}
                            <div className="relative" ref={eventDropdownRef}>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    {meta.eventFilterLabel}
                                </label>
                                <button
                                    type="button"
                                    onClick={() => setIsEventDropdownOpen(!isEventDropdownOpen)}
                                    className="w-full h-[50px] px-4 text-lg text-start border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white flex items-center justify-between"
                                >
                                    <span className="truncate">
                                        {selectedEventIds.length === 0
                                            ? meta.selectEventPrompt
                                            : selectedEventIds.length === events.length
                                                ? 'All Events'
                                                : `${selectedEventIds.length} event${selectedEventIds.length !== 1 ? 's' : ''} selected`}
                                    </span>
                                    <svg className={`w-5 h-5 transition-transform ${isEventDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                {isEventDropdownOpen && (
                                    <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                        {/* Select All / Deselect All */}
                                        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex gap-2">
                                            <button
                                                type="button"
                                                onClick={handleSelectAllEvents}
                                                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                                            >
                                                Select All
                                            </button>
                                            <span className="text-gray-300 dark:text-gray-600">|</span>
                                            <button
                                                type="button"
                                                onClick={handleDeselectAllEvents}
                                                className="text-sm text-gray-600 hover:text-gray-700 dark:text-gray-400"
                                            >
                                                Deselect All
                                            </button>
                                        </div>
                                        {events.length === 0 ? (
                                            <div className="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">
                                                No events available
                                            </div>
                                        ) : (
                                            sortByName(events).map(event => (
                                                <label
                                                    key={event.id}
                                                    className="flex items-center px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedEventIds.includes(event.id)}
                                                        onChange={() => handleEventToggle(event.id)}
                                                        className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                                    />
                                                    <span className="ms-3 text-gray-700 dark:text-gray-300">
                                                        {event.name}
                                                    </span>
                                                </label>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Loading State */}
                {loading && (
                    <Card className="mb-6">
                        <div className="p-8 text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                            <p className="mt-4 text-gray-600 dark:text-gray-400">{meta.loadingMessage}</p>
                        </div>
                    </Card>
                )}

                {/* Error State */}
                {error && (
                    <Card className="mb-6 border-danger-300 dark:border-danger-700">
                        <div className="p-6 flex items-center gap-3 text-danger-700 dark:text-danger-400">
                            <XCircleIcon className="w-6 h-6 flex-shrink-0" />
                            <p>{error}</p>
                        </div>
                    </Card>
                )}

                {/* Success Message */}
                {successMessage && (
                    <Card className="mb-6 border-success-300 dark:border-success-700 bg-success-50 dark:bg-success-900/20">
                        <div className="p-4 flex items-center gap-3 text-success-700 dark:text-success-400">
                            <CheckCircleIcon className="w-6 h-6 flex-shrink-0" />
                            <p>{successMessage}</p>
                        </div>
                    </Card>
                )}

                {/* Search Results */}
                {mode === 'search' && searchResults.length > 0 && !result && (
                    <Card className="mb-6">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    {meta.searchResultsTitle}
                                </h3>
                                {canAllocate && (
                                    <button
                                        onClick={() => setIsCreateVolunteerOpen(true)}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                                    >
                                        <PlusIcon className="w-4 h-4" />
                                        {meta.createVolunteerButton}
                                    </button>
                                )}
                            </div>
                            <div className="space-y-3">
                                {searchResults.map((searchResult) => (
                                    <div
                                        key={searchResult.volunteer.csid}
                                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                    >
                                        <div className="flex items-center gap-4">
                                            {searchResult.volunteer.profile_photo ? (
                                                <img
                                                    src={searchResult.volunteer.profile_photo}
                                                    alt={`${searchResult.volunteer.first_name} ${searchResult.volunteer.last_name}`}
                                                    className="w-12 h-12 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                                                    <PersonIcon className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                                                </div>
                                            )}
                                            <div>
                                                <div className="font-medium text-gray-900 dark:text-white">
                                                    {searchResult.volunteer.first_name} {searchResult.volunteer.last_name}
                                                </div>
                                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                                    {searchResult.volunteer.email && <span>{searchResult.volunteer.email}</span>}
                                                    {searchResult.volunteer.email && (searchResult.volunteer.international_volunteer || searchResult.volunteer.jamat_khana) && <span> • </span>}
                                                    {searchResult.volunteer.international_volunteer
                                                        ? <span className="text-primary-600 dark:text-primary-400">International</span>
                                                        : searchResult.volunteer.jamat_khana && <span>{searchResult.volunteer.jamat_khana}</span>
                                                    }
                                                </div>
                                                {searchResult.allocations.length > 0 && (
                                                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                                        {searchResult.allocations.reduce((t, a) => t + a.allocation_times.length, 0)} allocation(s)
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleSelectVolunteer(searchResult)}
                                            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            {meta.selectVolunteerButton}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                )}

                {/* No Search Results */}
                {mode === 'search' && !searching && searchResults.length === 0 && hasSearched && !result && !error && (
                    <Card className="mb-6">
                        <div className="p-8 text-center">
                            <SearchIcon className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                            <p className="text-gray-600 dark:text-gray-400 mb-4">{meta.noSearchResultsMessage}</p>
                            {canAllocate && (
                                <button
                                    onClick={() => setIsCreateVolunteerOpen(true)}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    <PlusIcon className="w-4 h-4" />
                                    {meta.createVolunteerButton}
                                </button>
                            )}
                        </div>
                    </Card>
                )}

                {/* Scan/Search to Begin Message */}
                {!loading && !searching && !error && !result && searchResults.length === 0 && !hasSearched && mode !== 'shift' && (
                    <Card className="mb-6">
                        <div className="p-12 text-center">
                            <PersonIcon className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                            <p className="text-lg text-gray-600 dark:text-gray-400">
                                {mode === 'scan' ? meta.scanToBeginMessage : meta.searchToBeginMessage}
                            </p>
                        </div>
                    </Card>
                )}

                {/* Shift View Content */}
                {mode === 'shift' && (
                    <>
                        {/* No areas message */}
                        {selectedEventIds.length > 0 && areas.length === 0 && (
                            <Card className="mb-6">
                                <div className="p-8 text-center">
                                    <ListIcon className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                                    <p className="text-gray-600 dark:text-gray-400">{meta.shiftViewNoAreasMessage}</p>
                                </div>
                            </Card>
                        )}

                        {/* No shifts message */}
                        {selectedAreaId && selectedAreaShifts.length === 0 && (
                            <Card className="mb-6">
                                <div className="p-8 text-center">
                                    <ListIcon className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                                    <p className="text-gray-600 dark:text-gray-400">{meta.shiftViewNoShiftsMessage}</p>
                                </div>
                            </Card>
                        )}

                        {/* Loading volunteers */}
                        {loadingShiftVolunteers && (
                            <Card className="mb-6">
                                <div className="p-8 text-center">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                                    <p className="mt-4 text-gray-600 dark:text-gray-400">{meta.shiftViewLoadingVolunteers}</p>
                                </div>
                            </Card>
                        )}

                        {/* Shift volunteers list */}
                        {shiftVolunteers && !loadingShiftVolunteers && (
                            <Card>
                                <div className="p-4 sm:p-6">
                                    {/* Shift header */}
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {shiftVolunteers.shift.title || shiftVolunteers.shift.duty?.name || 'Shift'}
                                            </h3>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {shiftVolunteers.shift.area.name} • {shiftVolunteers.shift.event.name}
                                            </p>
                                            {selectedShiftLeadInfo.length > 0 && (
                                                <div className="mt-1 space-y-0.5 text-sm text-gray-500 dark:text-gray-400">
                                                    {selectedShiftLeadInfo.map(info => (
                                                        <p key={info}>{info}</p>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                                            shiftVolunteers.stats.checked_in === shiftVolunteers.stats.accepted
                                                ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
                                                : shiftVolunteers.stats.checked_in > 0
                                                    ? 'bg-warn-100 text-warn-700 dark:bg-warn-900/30 dark:text-warn-400'
                                                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                        }`}>
                                            {meta.shiftViewVolunteerCount(shiftVolunteers.stats.checked_in, shiftVolunteers.stats.accepted)}
                                        </div>
                                    </div>

                                    {/* Volunteers list */}
                                    {shiftVolunteers.volunteers.length === 0 ? (
                                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                            {meta.shiftViewNoVolunteersMessage}
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {shiftVolunteers.volunteers.map((volunteer) => (
                                                <div
                                                    key={volunteer.allocation_time_id}
                                                    className={`p-4 rounded-lg border ${volunteer.checked_in_at
                                                        ? 'bg-success-50 dark:bg-success-900/10 border-success-200 dark:border-success-800'
                                                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                                                    }`}
                                                >
                                                    <div className="flex items-center justify-between gap-4">
                                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                                            {/* Profile photo */}
                                                            {volunteer.profile_photo ? (
                                                                <img
                                                                    src={volunteer.profile_photo}
                                                                    alt={`${volunteer.first_name} ${volunteer.last_name}`}
                                                                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                                                />
                                                            ) : (
                                                                <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                                                                    <PersonIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                                                                </div>
                                                            )}
                                                            <div className="min-w-0">
                                                                <div className="font-medium text-gray-900 dark:text-white truncate">
                                                                    {volunteer.first_name} {volunteer.last_name}
                                                                </div>
                                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                                                                    {volunteer.phone && (
                                                                        <span>{volunteer.phone}</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {/* Action button */}
                                                        <div className="flex-shrink-0">
                                                            {volunteer.checked_in_at ? (
                                                                <div className="text-end">
                                                                    <div className="flex items-center gap-1 text-success-600 dark:text-success-400">
                                                                        <CheckCircleIcon className="w-5 h-5" />
                                                                        <span className="text-sm hidden sm:inline">
                                                                            {meta.checkedInAtLabel(formatCheckedInTime(volunteer.checked_in_at))}
                                                                        </span>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleShiftViewUndoCheckIn(volunteer.allocation_time_id)}
                                                                        disabled={shiftViewCheckingInId === volunteer.allocation_time_id}
                                                                        className="mt-1 text-xs text-gray-500 hover:text-danger-600 dark:text-gray-400 dark:hover:text-danger-400"
                                                                    >
                                                                        {meta.undoCheckInButton}
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div className="flex flex-col sm:flex-row gap-2">
                                                                    <button
                                                                        onClick={() => handleShiftViewCheckIn(volunteer.allocation_time_id)}
                                                                        disabled={shiftViewCheckingInId === volunteer.allocation_time_id || deallocatingId === volunteer.allocation_time_id}
                                                                        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                                                    >
                                                                        {shiftViewCheckingInId === volunteer.allocation_time_id ? meta.checkingInButton : meta.checkInButton}
                                                                    </button>
                                                                    {canAllocate && shiftVolunteers && (
                                                                        <button
                                                                            onClick={() => handleDeallocateDuty({
                                                                                shiftId: shiftVolunteers.shift.id,
                                                                                volunteerId: volunteer.csid,
                                                                                allocationTimeId: volunteer.allocation_time_id,
                                                                                dutyName: shiftVolunteers.shift.title || shiftVolunteers.shift.duty?.name || 'this duty',
                                                                                status: volunteer.status,
                                                                                source: 'shift',
                                                                            })}
                                                                            disabled={shiftViewCheckingInId === volunteer.allocation_time_id || deallocatingId === volunteer.allocation_time_id}
                                                                            className="px-4 py-2 text-sm font-medium text-danger-700 bg-danger-50 border border-danger-200 rounded-lg hover:bg-danger-100 focus:outline-none focus:ring-2 focus:ring-danger-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap dark:bg-danger-900/20 dark:text-danger-300 dark:border-danger-800 dark:hover:bg-danger-900/30"
                                                                        >
                                                                            {deallocatingId === volunteer.allocation_time_id ? meta.deallocatingDutyButton : meta.deallocateDutyButton}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </Card>
                        )}

                        {/* Prompt to select area/shift */}
                        {selectedEventIds.length > 0 && areas.length > 0 && !selectedShiftId && !loadingShiftVolunteers && (
                            <Card className="mb-6">
                                <div className="p-12 text-center">
                                    <ListIcon className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
                                    <p className="text-lg text-gray-600 dark:text-gray-400">
                                        {!selectedAreaId ? meta.shiftViewSelectAreaPrompt : meta.shiftViewSelectShiftPrompt}
                                    </p>
                                </div>
                            </Card>
                        )}
                    </>
                )}

                {/* Volunteer Result */}
                {result && (
                    <>
                        {/* Volunteer Card */}
                        <Card className="mb-6">
                            <div className="p-4 sm:p-6">
                                <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
                                    {/* Profile Photo or Placeholder */}
                                    <div className="flex-shrink-0 flex items-center gap-4 sm:block">
                                        {result.volunteer.profile_photo ? (
                                            <img
                                                src={result.volunteer.profile_photo}
                                                alt={`${result.volunteer.first_name} ${result.volunteer.last_name}`}
                                                className="w-16 h-16 sm:w-24 sm:h-24 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                                <PersonIcon className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 dark:text-gray-500" />
                                            </div>
                                        )}
                                        {/* Name shown next to photo on mobile */}
                                        <div className="sm:hidden">
                                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                                {result.volunteer.first_name} {result.volunteer.last_name}
                                            </h2>
                                            {/* Check-in summary badge - mobile */}
                                            {totalAllocations > 0 && (
                                                <div className={`mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-medium ${checkedInCount === totalAllocations
                                                    ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
                                                    : checkedInCount > 0
                                                        ? 'bg-warn-100 text-warn-700 dark:bg-warn-900/30 dark:text-warn-400'
                                                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                                    }`}>
                                                    {checkedInCount}/{totalAllocations} {meta.checkedInLabel}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Volunteer Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="hidden sm:flex items-center justify-between">
                                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                                {result.volunteer.first_name} {result.volunteer.last_name}
                                            </h2>
                                            {/* Check-in summary badge - desktop */}
                                            {totalAllocations > 0 && (
                                                <div className={`px-3 py-1 rounded-full text-sm font-medium ${checkedInCount === totalAllocations
                                                    ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400'
                                                    : checkedInCount > 0
                                                        ? 'bg-warn-100 text-warn-700 dark:bg-warn-900/30 dark:text-warn-400'
                                                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                                    }`}>
                                                    {checkedInCount}/{totalAllocations} {meta.checkedInLabel}
                                                </div>
                                            )}
                                        </div>

                                        <div className="sm:mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 text-sm">
                                            {result.volunteer.email && (
                                                <div className="text-gray-600 dark:text-gray-400 truncate">
                                                    <span className="font-medium">{meta.emailLabel}:</span> {result.volunteer.email}
                                                </div>
                                            )}
                                            {result.volunteer.phone && (
                                                <div className="text-gray-600 dark:text-gray-400">
                                                    <span className="font-medium">{meta.phoneLabel}:</span> {result.volunteer.phone}
                                                </div>
                                            )}
                                            {result.volunteer.country && (
                                                <div className="text-gray-600 dark:text-gray-400">
                                                    <span className="font-medium">{meta.countryLabel}:</span> {result.volunteer.country}
                                                </div>
                                            )}
                                            {result.volunteer.jamat_khana && (
                                                <div className="text-gray-600 dark:text-gray-400">
                                                    <span className="font-medium">{meta.jamatkhanaLabel}:</span> {result.volunteer.jamat_khana}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Allocations */}
                        <Card>
                            <div className="p-4 sm:p-6">
                                {result.volunteer.eligible_for_duty != null && !result.volunteer.eligible_for_duty ? (
                                    /* Reserved volunteer message */
                                    <div className="text-center py-8">
                                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-warn-100 dark:bg-warn-900/30 mb-4">
                                            <XCircleIcon className="w-8 h-8 text-warn-600 dark:text-warn-400" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                            Reserved
                                        </h3>
                                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                                            Please speak to Faisal Khowaja
                                        </p>
                                        <button
                                            onClick={handleScanAnother}
                                            className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                                        >
                                            {meta.scanAnotherButton}
                                        </button>
                                    </div>
                                ) : (
                                    /* Normal allocations view */
                                    <>
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        {meta.allocationsTitle}
                                    </h3>
                                    <div className="flex items-center gap-2">
                                        {canAllocate && selectedEventIds.length > 0 && (
                                            <button
                                                onClick={() => setIsQuickAllocateOpen(true)}
                                                className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium text-white bg-success-600 rounded-lg hover:bg-success-700 focus:outline-none focus:ring-2 focus:ring-success-500 flex items-center justify-center gap-1"
                                            >
                                                <PlusIcon className="w-4 h-4" />
                                                <span className="hidden xs:inline">{meta.allocateDutyButton}</span>
                                                <span className="xs:hidden">Allocate</span>
                                            </button>
                                        )}
                                        <button
                                            onClick={handleScanAnother}
                                            className="flex-1 sm:flex-none px-3 sm:px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 whitespace-nowrap"
                                        >
                                            {meta.scanAnotherButton}
                                        </button>
                                    </div>
                                </div>

                                {result.allocations.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                        {selectedEventIds.length > 0 ? meta.noAllocationsForEventMessage : meta.noAllocationsMessage}
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {result.allocations
                                            .flatMap(allocation =>
                                                allocation.allocation_times
                                                    .filter(time => time.status !== false)
                                                    .map(time => ({
                                                        allocation,
                                                        time
                                                    }))
                                            )
                                            .sort((a, b) => {
                                                // Sort by start_time
                                                const timeA = a.time.start_time || '';
                                                const timeB = b.time.start_time || '';
                                                return timeA.localeCompare(timeB);
                                            })
                                            .map(({ allocation, time }) => (
                                                <div
                                                    key={time.id}
                                                    className={`p-4 rounded-lg border ${time.checked_in_at
                                                        ? 'bg-success-50 dark:bg-success-900/10 border-success-200 dark:border-success-800'
                                                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                                                        }`}
                                                    style={{ borderColor: allocation.shift?.area?.access_color?.color, borderWidth: 5 }}
                                                >
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div className="flex-1 min-w-0">
                                                            {/* Shift name - most prominent */}
                                                            <div className="font-medium text-gray-900 dark:text-white">
                                                                {allocation.shift.title || allocation.shift.duty?.name || 'Shift'}
                                                            </div>
                                                            {/* Event and Area */}
                                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                                <span>{allocation.shift.area.event.name}</span>
                                                                <span className="flex items-center gap-1">
                                                                    <LocationIcon className="w-4 h-4" />
                                                                    {allocation.shift.area.name}
                                                                </span>
                                                            </div>
                                                            {getShiftLeadInfo(allocation.shift.lead, allocation.shift.deputy_lead).length > 0 && (
                                                                <div className="mt-2 space-y-0.5 text-sm text-gray-600 dark:text-gray-400">
                                                                    {getShiftLeadInfo(allocation.shift.lead, allocation.shift.deputy_lead).map(info => (
                                                                        <p key={info}>{info}</p>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {/* Status */}
                                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2">
                                                                {getStatusBadge(time.status)}
                                                            </div>
                                                        </div>
                                                        {/* Action button - always visible */}
                                                        <div className="flex-shrink-0">
                                                            {time.checked_in_at ? (
                                                                <div className="text-end">
                                                                    <div className="flex items-center gap-1 text-success-600 dark:text-success-400">
                                                                        <CheckCircleIcon className="w-5 h-5" />
                                                                        <span className="text-sm">
                                                                            {meta.checkedInAtLabel(formatCheckedInTime(time.checked_in_at))}
                                                                        </span>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleUndoCheckIn(time.id)}
                                                                        disabled={checkingInId === time.id}
                                                                        className="mt-1 text-xs text-gray-500 hover:text-danger-600 dark:text-gray-400 dark:hover:text-danger-400"
                                                                    >
                                                                        {meta.undoCheckInButton}
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div className="flex flex-col sm:flex-row gap-2">
                                                                    <button
                                                                        onClick={() => handleCheckIn(time.id)}
                                                                        disabled={checkingInId === time.id || deallocatingId === time.id}
                                                                        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                                                    >
                                                                        {checkingInId === time.id ? meta.checkingInButton : meta.checkInButton}
                                                                    </button>
                                                                    {canAllocate && (
                                                                        <button
                                                                            onClick={() => handleDeallocateDuty({
                                                                                shiftId: allocation.shift.id,
                                                                                volunteerId: result.volunteer.csid,
                                                                                allocationTimeId: time.id,
                                                                                dutyName: allocation.shift.title || allocation.shift.duty?.name || 'this duty',
                                                                                status: time.status,
                                                                                source: 'result',
                                                                            })}
                                                                            disabled={checkingInId === time.id || deallocatingId === time.id}
                                                                            className="px-4 py-2 text-sm font-medium text-danger-700 bg-danger-50 border border-danger-200 rounded-lg hover:bg-danger-100 focus:outline-none focus:ring-2 focus:ring-danger-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap dark:bg-danger-900/20 dark:text-danger-300 dark:border-danger-800 dark:hover:bg-danger-900/30"
                                                                        >
                                                                            {deallocatingId === time.id ? meta.deallocatingDutyButton : meta.deallocateDutyButton}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                )}
                                </>
                                )}
                            </div>
                        </Card>
                    </>
                )}

                {/* Quick Allocate Modal */}
                {result && selectedEventIds.length > 0 && (
                    <QuickAllocateModal
                        isOpen={isQuickAllocateOpen}
                        onClose={() => setIsQuickAllocateOpen(false)}
                        volunteerId={result.volunteer.csid}
                        volunteerName={`${result.volunteer.first_name} ${result.volunteer.last_name}`}
                        eventIds={selectedEventIds}
                        onAllocationSuccess={() => refreshVolunteerAllocations(result.volunteer.csid)}
                    />
                )}

                {/* Camera Scanner Modal */}
                <CameraScanner
                    isOpen={showCameraScanner}
                    onClose={() => setShowCameraScanner(false)}
                    onScan={(barcode) => {
                        setShowCameraScanner(false)
                        handleScan(barcode)
                    }}
                    title={meta.cameraScannerTitle}
                    hint={meta.cameraScannerHint}
                    permissionDeniedMessage={meta.cameraPermissionDenied}
                    notSupportedMessage={meta.cameraNotSupported}
                    closeButtonLabel={meta.closeCameraButton}
                />

                {/* Create Volunteer Modal */}
                <CreateVolunteerModal
                    isOpen={isCreateVolunteerOpen}
                    onClose={() => setIsCreateVolunteerOpen(false)}
                    onVolunteerCreated={handleVolunteerCreated}
                />
            </div>
        </div>
    )
}
